import React, { useEffect, useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { bindActionCreators } from 'redux';
import * as actions from 'containers/ProjectTools/Notes/actions';
import {
  createNoteTemplateFolder as actionCreateNoteTemplateFolder,
} from 'containers/Settings/Projects/actions';
import { pushSiteNotification } from 'containers/App/actions';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import DropdownButton from 'components/Button/DropdownButton/DropdownButton';
import APDropdown from 'components/Dropdown/APDropdown';
import Drawer from 'components/Drawer';
import { MobileSm, DefaultSm } from 'components/MediaQueries';
import { NOTE_RESTRICTIONS } from 'utils/constants';
import APModal from 'components/Modal/APModal';
import InputField from 'components/FormFields/InputField';
import { isUrlValid, copyToClipboard, isFileImageExtension } from 'utils/general';
import FormSelect from 'components/FormFields/FormSelect';
import ImageUploadField from 'components/FormFields/ImageUploadField';
import { streamDownload } from 'api/notes';
import ShareModal from 'pages/Projects/Tools/Share/ShareModal';
import SharePlusModal from 'pages/Projects/Tools/Share/SharePlusModal';
import { downloadPdf, downloadImage } from 'pages/Notes/NotePreview/NotePreview';
import { getPreviewUrl } from './utils/getPreviewUrl';
import SaveTemplateModal from './Notes.SaveTemplateModal';
import { DeleteTemplateModal } from '../BusinessSettings/Templates/Notes/NoteTemplate.OptionsButton';

export const NotesOptionsButton = ({ id,
  url,
  imageUrls,
  image,
  originalImage,
  setImageUpdating,
  note,
  renameAction,
  setPermissions,
  isOwner,
  notesEdit,
 }) => {
  const [opened, setOpened] = useState(false);
  const [target, setTarget] = useState(null);

  const [deleteConfirmOpened, setDeleteConfirmOpened] = useState(false);
  const [urlModalOpened, setUrlModalOpened] = useState(false);
  const [templateModalOpened, setTemplateModalOpened] = useState(false);
  const [imageCropperOpened, setImageCropperOpen] = useState(false);
  const [renameModalOpened, setRenameModalOpened] = useState(false);

  const previewUrl = useMemo(() => getPreviewUrl(note.id), [note.id]);

  const getRestrictionValue = () => {
    if(note.clientReadOnly && note.plannerOnly) {
      return 'None';
    } if (note.clientReadOnly && !note.plannerOnly) {
      return 'ClientEditingLocked';
    } if (!note.clientReadOnly && note.plannerOnly) {
      return 'VisibleToOrgMembersOnly';
    };
    return 'None';
  };

  const [restriction, setRestriction] = useState(getRestrictionValue());

  const [isPreparingDownload, setIsPreparingDownload] = useState(false);
  const [errorModalOpened, setErrorModalOpened] = useState(false);
  const [errorModalMessage, setErrorModalMessage] = useState(null);
  const [errorModalTitle, setErrorModalTitle] = useState(null);
  const [errorModalButtonTitle, setErrorModalButtonTitle] = useState(null);
  const [showShareModal, toggleShareModalOpen] = useState(false);
  const [showSharePlusModal, toggleSharePlusModalOpen] = useState(false);

  const templateFolders = useSelector((state) => state.settings.projectSettings.noteTemplateFolders);

  // #TODO: This can possibly be refactored into a reducer so it can be accessed
  // via global state rather than duplicating this logic where it is needed.
  // Share permissions.
  const { organization, permissions } = useSelector((state) => state.global.userInfo);
  const { features, organizationId: projectOrgId } = useSelector((state) => state.projectTools.project || {});
  const isInOrg = organization && projectOrgId === organization.id;
  const notesFeature = useMemo(
    () => Array.isArray(features) ?
      features.find(feat => feat.id === 'notes_enabled') : null,
    [features],
  );
  const designStudioFeature = useMemo(
    () => Array.isArray(features) ?
      features.find(feat => feat.id === 'design_studio_enabled') : null,
    [features],
  );
  const isPlanningPartner = !organization || projectOrgId !== organization.id;
  const { features: featuresReleased } = useSelector((state) => state.projects.currentProject);
  const { designStudioReleased, notesReleased } = featuresReleased || {};
  const weddingPermission = useSelector((state) => state.projects.weddingPermission);
  const designStudioViewPermission = weddingPermission &&
    weddingPermission.find(p => p.name === 'design-studio.view-tool');
  const hasStudioShareViewingPermission = designStudioViewPermission ? designStudioViewPermission.value : false;
  const canViewDesignStudio = isInOrg || hasStudioShareViewingPermission && designStudioReleased;
  // Org owner/members of project can always share.
  // Only show share plus if notes is enabled and is released
  // or studio share is enabled and is released.
  // or is client and can view studio shares.
  const notesIsReleased = notesFeature?.checked && notesReleased;
  const showShare = isInOrg || notesIsReleased;
  const showSharePlus =  isInOrg || (
    designStudioFeature?.checked && isPlanningPartner && canViewDesignStudio && notesIsReleased
  );

  const dispatch = useDispatch();
  const {
    deleteNote,
    updateUrl,
    saveTemplate,
    updateNote,
    renameNote,
    restrictionsNote,
    duplicateNote,
    createNoteTemplateFolder,
    pushNotification,
  } = bindActionCreators({
    ...actions,
    createNoteTemplateFolder: actionCreateNoteTemplateFolder,
    pushNotification: pushSiteNotification,
  }, dispatch);

  const dismiss = () => {
    setOpened(false);
    target?.classList.add('hover-expose');
  };

  const renderOptions = () => {
    const deleteItem = () => {
      setDeleteConfirmOpened(true);
      dismiss();
    };

    const restrictionOnChange = (e) => {
      setRestriction(e.currentTarget.value);
      switch (e.currentTarget.value) {
        case 'VisibleToOrgMembersOnly':
          restrictionsNote({ id, clientReadOnly: false, plannerOnly: true });
          setPermissions(false, true);
          break;
        case 'ClientEditingLocked':
          restrictionsNote({ id, clientReadOnly: true, plannerOnly: false });
          setPermissions(true, false);
          if (note.isShortcut) {
            setErrorModalOpened(true);
            setErrorModalTitle('Heads up!');
            setErrorModalMessage(`Although client editing is locked for this shortcut in Aisle Planner, you may
              still need to manage outside permissions separately.`);
            setErrorModalButtonTitle('Got It!');
          }
          setOpened(false);
          break;
        case 'None':
          restrictionsNote({ id, clientReadOnly: false, plannerOnly: false });
          setPermissions(false, false);
          break;
        default:
          break;
      };
    };

    const copyLink = () => {
      copyToClipboard(url || previewUrl);

      pushNotification({
        message: 'Note link copied to clipboard.',
        time: 3000,
      });
      dismiss();
    };

    const shareModal = (e) => {
      e.stopPropagation();
      toggleShareModalOpen(true);
      dismiss();
    };

    const sharePlusModal = (e) => {
      e.stopPropagation();
      toggleSharePlusModalOpen(true);
      dismiss();
    };

    const editCoverImage = (e) => {
      e.stopPropagation();
      setImageCropperOpen(true);
      dismiss();
    };

    const editURL = (e) => {
      e.stopPropagation();
      setUrlModalOpened(true);
      dismiss();
    };

    const renameItem = (e) => {
      e.stopPropagation();
      renameAction();
      dismiss();
    };

    const saveAsTemplate = (e) => {
      e.stopPropagation();
      setTemplateModalOpened(true);
      dismiss();
    };

    const preview = (e)  => {
      e.stopPropagation();
      window.open(previewUrl, '_blank');
      dismiss();
    };

    const download = (e) => {
      e.stopPropagation();
      // Directly download file if file is uploaded as the note.
      if (note.isFileUpload && note.fileViewerUrl && note.file) {
        const { originalFilename: filename } = note.file;
        const fileUrl = note.fileViewerUrl;

        if (isFileImageExtension(filename)) {
          downloadImage(fileUrl, filename);
        } else {
          downloadPdf(fileUrl, filename);
        }
        dismiss();
      } else {
        const params = {};
        params.note_id = id;
        setIsPreparingDownload(true);

        streamDownload({ weddingId: note.weddingId, params })
          .then((response) => {
            if (response.status !== 401) window.location.replace(response.data.url);
            setIsPreparingDownload(false);
            dismiss();
          })
          .catch(() => {
            setIsPreparingDownload(false);
            setErrorModalOpened(true);
            setErrorModalTitle('Something went wrong.');
            setErrorModalMessage("Sorry, we couldn't prepare the download. Please try again.");
            setErrorModalButtonTitle('Close');
          });
      }
    };

    const duplicateItem = () => {
      duplicateNote({ id });
      dismiss();
    };

    const showMenuItem = isOwner || (!note.clientReadOnly && notesEdit);
    // Clients should never be able to save as template.
    // Org members should not be able to save templates if they don't have access to templates.
    const showSaveAsTemplate = isOwner &&
      permissions && permissions['settings.template.edit'] && permissions['settings.template.view'];

    const menuItems = [
      ... showMenuItem ? [{ title: 'Edit cover image', onClick: editCoverImage }] : [],
      ...(!url ? [{ title: 'Preview', onClick: preview }] : []),
      ... showShare ? [{ title: 'Copy Link', onClick: copyLink }] : [],
      ... showShare ? [{ title: 'Share This Note', onClick: shareModal }] : [],
      ... showSharePlus ? [{ title: 'Share +', onClick: sharePlusModal }] : [],
      ... (showMenuItem && url) ? [{ title: 'Edit url', onClick: editURL }] : [],
      ... (showMenuItem && !url) ? [{ title: 'Duplicate', onClick: duplicateItem }] : [],
      ... showMenuItem ? [{ title: 'Rename', onClick: renameItem }] : [],
      ... showSaveAsTemplate ? [{ title: 'Save as template', onClick: saveAsTemplate }] : [],
      ... showMenuItem ? [{ title: 'Delete', onClick: deleteItem }] : [],
      ... url ? [] : [{
        title: 'Download',
        onClick: download,
        customClasses: ['download-link', 'border-top', isPreparingDownload ? 'button--add loading' : ''],
      }],
    ];

    return (
      <>
        {menuItems.map(({ title, onClick, customClasses }, index) => {
          return (
            <button
              className={clsx(
                'button--unstyled',
                'button--menu',
                customClasses,
              )}
              onClick={onClick}
              key={index}
            >
              {title}
            </button>
          );
        })}

        {isOwner ? (
          <div className={clsx('ap-dropdown__menu-section', 'border-top')}>
            <FormSelect
              onChange={restrictionOnChange}
              emptyValue="None"
              value={restriction}
              name="Restrictions"
              className="note-restriction-dropdown"
              scrollLockOnOpen={false}
              root="options-restrictions"
            >
              {NOTE_RESTRICTIONS.map(item => renderOption(item.value,item.label))}
            </FormSelect>
          </div>
        ) : null}
      </>
    );
  };

  const errorModalCloseHandler = () => {
    setErrorModalOpened(false);
    setErrorModalMessage(null);
  };

  const trigger = (
    <DropdownButton className="button--unstyled button--options">
      <FontAwesomeIcon icon={['fa', 'ellipsis-v']} />
    </DropdownButton>
  );

  return (
    <>
      <DefaultSm>
        <APDropdown
          isOpen={opened}
          show={() => setOpened(true)}
          dismiss={() => {
            setOpened(false);
            setTarget(null);
          }}
          triggerButton={trigger}
          backdrop={false}
          scrollLockOnOpen={false}
        >
          {renderOptions()}
        </APDropdown>
      </DefaultSm>
      <MobileSm>
        <Drawer
          isOpen={opened}
          show={() => setOpened(true)}
          dismiss={dismiss}
          position="bottom"
          triggerButton={trigger}
        >
          {renderOptions()}
        </Drawer>
      </MobileSm>
      {deleteConfirmOpened && (
        <DeleteTemplateModal
          opened={deleteConfirmOpened}
          setOpened={setDeleteConfirmOpened}
          id={id}
          isShortcut={note?.isShortcut}
          deleteNoteTemplate={deleteNote}
          isTemplate={false}
        />
      )}
      {imageCropperOpened && (
        <CoverImageModal
          image={image}
          imageUrls={imageUrls}
          originalImage={originalImage}
          setImageUpdating={setImageUpdating}
          openImageCropper={imageCropperOpened}
          setImageCropperOpen={setImageCropperOpen}
          updateNote={updateNote}
          note={note}
        />
      )}
      {urlModalOpened && (
        <URLModal
          urlModalOpened={urlModalOpened}
          setUrlModalOpened={setUrlModalOpened}
          url={url}
          id={id}
          updateUrl={updateUrl}
        />
      )}
      {renameModalOpened && (
        <RenameModal
          renameModalOpened={renameModalOpened}
          setRenameModalOpened={setRenameModalOpened}
          title={note.title}
          id={id}
          renameNote={renameNote}
        />
      )}
      {templateModalOpened && (
        <SaveTemplateModal
          note={note}
          templateModalOpened={templateModalOpened}
          setTemplateModalOpened={setTemplateModalOpened}
          templateFolders={templateFolders}
          saveTemplate={saveTemplate}
          createNoteTemplateFolder={createNoteTemplateFolder}
        />
      )}
      {errorModalOpened && (
        <APModal
          open={errorModalOpened}
          width={620}
          height={300}
          onClose={errorModalCloseHandler}
          className="ap-modal--note-options-confirmation"
          size="narrow"
          title={errorModalTitle}
          actions={(
            <button
              onClick={errorModalCloseHandler}
              className="button"
            >
              {errorModalButtonTitle}
            </button>
          )}
        >
          {errorModalMessage}
        </APModal>
      )}
      {showShareModal && (
        /* have to add this to prevent note cards from dragging background in this modal */
        <div onPointerDown={(e) => e.stopPropagation()}>
          <ShareModal
            open={showShareModal}
            onClose={() => toggleShareModalOpen(false)}
            note={note}
          />
        </div>
      )}
      {showSharePlusModal && (
        /* have to add this to prevent note cards from dragging background in this modal */
        <div onPointerDown={(e) => e.stopPropagation()}>
          <SharePlusModal
            open={showSharePlusModal}
            onClose={() => toggleSharePlusModalOpen(false)}
            currentNote={note}
          />
        </div>
      )}
    </>
  );
};

const RenameModal = ({ renameModalOpened, setRenameModalOpened, title, id, renameNote }) => {
  const [noteTitle, setNoteTitle] = useState(title);
  const [error, setError] = useState('');

  useEffect(() => {
    if (noteTitle) {
      setError('');
    }
  }, [noteTitle]);

  const onSave = () => {
    if (noteTitle) {
      renameNote({ id, name: noteTitle });
      setRenameModalOpened(false);
    } else {
      setError('Please fill the note title.');
    }
  };

  const onCancel = () => {
    setRenameModalOpened(false);
  };

  const onChange = (event) => {
    setNoteTitle(event.target.value);
  };

  const modalActions = [{ onClick: onSave, title: 'Save' }];;

  const modalContent = (
    <InputField
      name="note_title"
      label="Title"
      defaultValue={noteTitle}
      onChange={onChange}
      onPointerDown={(e) => e.stopPropagation()}
      showError={!noteTitle}
    />
  );

  return renderModal(
    renameModalOpened,
    'Edit your title',
    'edit-note-title',
    modalActions,
    onCancel,
    modalContent,
    error,
  );
};

const URLModal = ({ urlModalOpened, setUrlModalOpened, url, id, updateUrl }) => {
  const [shortcutUrl, setShortcutUrl] = useState(url);
  const [isShortcutUrlValid, setShortcutUrlValid] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isShortcutUrlValid) {
      setError('');
    }
  }, [isShortcutUrlValid]);

  const onSave = () => {
    if (shortcutUrl && isShortcutUrlValid) {
      updateUrl({ id, url: shortcutUrl });
      setUrlModalOpened(false);
    } else {
      setError('Please review and update your website format to continue.');
    }
  };

  const onCancel = () => {
    setUrlModalOpened(false);
  };

  const onChange = (event) => {
    setShortcutUrl(event.target.value);
    setShortcutUrlValid(isUrlValid(event.target.value));
  };

  const modalActions = [{ onClick: onSave, title: 'Save' }];

  const modalContent = (
    <InputField
      name="note_shortcut"
      label="Shortcut"
      value={shortcutUrl}
      onChange={onChange}
      onPointerDown={(e) => e.stopPropagation()}
      onBlur={() => {
        if (shortcutUrl && !shortcutUrl.includes('https://')) {
          setShortcutUrl(`https://${shortcutUrl}`);
        }
      }}
      showError={!isShortcutUrlValid}
    />
  );

  return renderModal(
    urlModalOpened,
    'Edit your URL.',
    'edit-note-url',
    modalActions,
    onCancel,
    modalContent,
    error,
  );
};

const renderOption = (value, label, restProps) => {
  return (
    <option
      key={`move-to-${value}`}
      value={value}
      {...restProps}
    >
      {label}
    </option>
  );
};

const renderModal = (
  modalOpened,
  title,
  name,
  modalActions,
  onClose,
  modalContent,
  error,
) => {

  const renderModalActions = () => {
    return (
      <>
        {modalActions.map((item, index) => (
          <button
            className="button"
            onClick={item.onClick}
            key={index}
          >
            {item.title}
          </button>
        ))}
      </>
    );
  };

  return (
    <APModal
      open={modalOpened}
      title={title}
      name={name}
      onClose={onClose}
      actions={renderModalActions()}
    >
      {modalContent}
      <section className='note-error'>
        {error}
      </section>
    </APModal>
  );
};

const CoverImageModal = ({ image, imageUrls, originalImage, setImageUpdating,
  openImageCropper, setImageCropperOpen, note, updateNote }) => {
  const [imageKey, setImageKey] = useState(image?.key);
  const [imageUrl, setImageUrl] = useState(imageUrls?.original?.url);
  const [originalImageKey, setOriginalImageKey] = useState(originalImage?.key);

  const updateImageKey = (newImageKey) => {
    if (!newImageKey) {
      updateNote({ id: note.id, noteBody: { ...note, imageKey: 'delete' } });
      setImageUpdating(false);
    }
    else if (imageKey !== newImageKey) {
      updateNote({ id: note.id, noteBody: { ...note, imageKey: newImageKey } });
      setImageUpdating(false);
    };
    setImageKey(newImageKey);
  };

  return (
    <ImageUploadField
      label='Cover image'
      imageUploadType='shortcut'
      imageUrl={imageUrl}
      originalImageKey={originalImageKey}
      updateImageKey={updateImageKey}
      updateImageUrl={setImageUrl}
      updateOriginalImageKey={setOriginalImageKey}
      imageInfoPath='wedding/note/images'
      imageProcessPath='wedding/note/image'
      setImageUpdating={setImageUpdating}
      cropperOnly
      openCropper={openImageCropper}
      setImageModalOpen={setImageCropperOpen}
      imageCropperUploadText='Drag your image here'
      cropperConfig={image?.cropPosition}
    />
  );
};
